import { Config, fetchJSON, PROVINCE, fetchSponsoredPlacement } from '@ratehub/base-ui';

import CREDIT_SCORES from '../definitions/CreditScores';
import REWARD_TYPES from '../definitions/RewardTypes';
import USER_CATEGORIES from '../definitions/UserCategories';
import NETWORKS from '../definitions/Networks';
import REWARDS_PROGRAMS from '../definitions/RewardsPrograms';
import TRAVEL_FEATURES from '../definitions/TravelFeatures';
import SCENARIOS from '../definitions/Scenarios';
import CreditCardShape from '../definitions/CreditCardShape';
import createCreditCardsAndVerify from './createCreditCardsAndVerify';


/**
 * Retrieve credit cards from API with supplied parameters.
 * Note: this is for MULTIPLE cards. There is also fetchCreditCard() for a single.
 * @param {object} params - params object
 * @param {object} [abortControllerSignal] - an AbortSignal object instance to cancel ongoing fetch request
 * @returns {Promise<{ creditCards: Array<CreditCardShape>, fetchedTypes: Array<string> }>}
 *              object with array of credit cards and array of fetched
 *              product types if multiple types were fetched
 */
async function fetchCreditCards(params, abortControllerSignal) {
    // safely extract the sponsoredPlacementSlug
    const sponsoredPlacementSlug = params?.sponsoredPlacementSlug;

    const isUsingNewSponsoredPlacementSystem = !!(Config?.ENABLE_CC_SPONSORED_PLACEMENTS && sponsoredPlacementSlug);

    const apiParams = {
        language: params.language,
        // Handle conversion of Yukon province code
        province: params.province === PROVINCE.YUKON
            ? DEPRECATED_YUKON_PROVINCE_CODE
            : params.province,
        ...(!isUsingNewSponsoredPlacementSystem && params.customId && { customId: params.customId }),
        // To Disable old system where sponsored product is placed by the backend API
        disableSponsored: isUsingNewSponsoredPlacementSystem,
    };

    // other CC-fetch functions may take this as an argument, but since several of these values
    // may already exist in `params`, we’ll extract them from there instead
    // AND rely on createCardInfoFromAPIResponse() for filling in the rest
    const userPreferences = {
        ...(params.noAnnualFee && { noAnnualFee: params.noAnnualFee }),
        ...(params.income && { income: params.income }),
        ...(params.monthlySpendingAverage && { monthlySpendingAverage: params.monthlySpendingAverage }),
        ...(params.monthlySpending && { monthlySpending: params.monthlySpending }),
        ...(params.lowInterestMonthlyBalance && { lowInterestMonthlyBalance: params.lowInterestMonthlyBalance }),
        ...(params.balanceTransferAmount && { balanceTransferAmount: params.balanceTransferAmount }),
        ...(params?.features?.length > 0 && { chosenFeatures: params.features }),
        ...(params.requiresGiftOffer && { requiresGiftOffer: params.requiresGiftOffer }),
    };

    // Default API call is based on params.scenario
    let apiURL = `${Config.CC_API_URL}/${params.scenario}`;

    if (params.noAnnualFee) {
        apiParams.noFee = params.noAnnualFee;
    }

    if (typeof params.exactCreditScore === 'number') {
        apiParams.exactCreditScore = params.exactCreditScore;
    } else if (params.creditScore && params.creditScore !== CREDIT_SCORES.NONE) {
        apiParams.creditScore = params.creditScore;
    }

    if (params.income > 0) {
        apiParams.income = params.income;
    }

    if (params.bank) {
        apiParams.provider = params.bank;
    }

    if (params.monthlySpending) {
        apiParams.spendingProfile = params.monthlySpending;
    }

    if (params.rewardType && params.rewardType !== REWARD_TYPES.ALL) {
        apiParams.rewardsType = params.rewardType;
    }

    if (params?.features?.length > 0) {
        apiParams.features = params.features.map(feature => API_FEATURE_MAP[feature]);
    }

    if (params.rewardsProgram && params.rewardsProgram !== REWARDS_PROGRAMS.ALL) {
        apiParams.pointSystem = params.rewardsProgram;
    }

    if (params.requestedTypes?.length) {
        apiParams.requestedTypes = params.requestedTypes;
        apiParams.fallbackTypes = params.fallbackTypes;

        apiURL = `${Config.CC_API_URL}/card-mix`;
    }

    if (params.sortBy) {
        apiParams.sortBy = params.sortBy;
    }

    if (params.requiresGiftOffer) {
        apiParams.giftOffer = params.requiresGiftOffer;
    }

    if (params.requiresReferralOffer) {
        apiParams.referralOffer = params.requiresReferralOffer;
    }

    if (Object.values(USER_CATEGORIES).includes(params.userCategory)) {
        apiParams.userCategory = params.userCategory;
    }

    if (Object.keys(NETWORKS_NAMES).includes(params.network)) {
        apiParams.network = NETWORKS_NAMES[params.network];
    }

    if (params.prioritizeSponsored) {
        apiParams.prioritizeSponsored = params.prioritizeSponsored;
    }

    // TODO: Temporary parameter to pass through to enable ML approval data set
    // until proper fetch endpoint approach is redesigned AC-453
    if (params.documentId) {
        apiParams.documentId = params.documentId;
    }
    if (params.profileId) {
        apiParams.profileId = params.profileId;
    }

    if (typeof params.draftMode === 'boolean') {
        apiParams.draftMode = params.draftMode;
    }


    // For /card-set endpoint
    if (params.ids) {
        apiParams.ids = !Array.isArray(params.ids)
            ? params.ids.split(',')
            : params.ids;

        apiURL = `${Config.CC_API_URL}/card-set`;
    }

    const json = await fetchJSON({
        url: apiURL,
        options: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(apiParams),
        },
        signal: abortControllerSignal,
    }, 'fetchCreditCards');

    // Default to empty array if no products returned
    const products = json?.data?.creditCards ?? [];

    // Only proceed if sponsored placements are enabled and a slug is given
    if (isUsingNewSponsoredPlacementSystem) {
        const sponsoredPlacements = await fetchSponsoredPlacement(sponsoredPlacementSlug, apiParams.province);

        let filteredPlacements = sponsoredPlacements ?? [];

        if (params?.scenario !== SCENARIOS.REWARDS) {
            // Look up the corresponding section name for the current scenario
            const scenarioSection = scenarioToSectionMap?.[params?.scenario] ?? null;

            // Filter sponsored placements to include only those that match the current scenario's section.
            // We are doing this because some credit cards do exist in more than one senario especifically
            // when used in TabbedProductTableCreditCardScenario
            filteredPlacements = filteredPlacements.filter(
                placement => placement?.section === scenarioSection
            );
        }

        // match the slug of a sponsored product to the slug (product?.ids?.primary) of one of the listed products
        for (const placement of filteredPlacements) {
            const sponsoredProductSlug = placement?.productSlug;

            if (sponsoredProductSlug) {
                // find its index in the list
                const index = products.findIndex(product => product?.ids?.primary === sponsoredProductSlug);
                if (index > -1) {
                    // remove the matched product from products and stores the removed product in matchedProduct
                    const [ matchedProduct ] = products.splice(index, 1);

                    // make it sponsored and insert it at the start of the `products` array
                    matchedProduct.sponsored.table = true;
                    products.unshift(matchedProduct);
                    break;
                }
            }
        }
    }

    return {
        creditCards: products.length > 0
            ? createCreditCardsAndVerify(products, userPreferences, json.data.valid_approval_likelihood_document)
            : [],
        // If multiple product types were fetched need to populate array with which ones
        fetchedTypes: Array.isArray(json?.data?.fetchedTypes)
            ? json.data.fetchedTypes
            : [],
    };
}

// todo: update these values elsewhere + remove this mapping
const API_FEATURE_MAP = {
    [TRAVEL_FEATURES.BAGGAGE_DELAY]:        'baggage-delay',
    [TRAVEL_FEATURES.BAGGAGE_LOSS]:         'baggage-loss',
    [TRAVEL_FEATURES.CAR_RENTAL]:           'car-rental',
    [TRAVEL_FEATURES.EXTENDED_WARRANTY]:    'extended-warranty',
    [TRAVEL_FEATURES.FLIGHT_DELAY]:         'flight-delay',
    [TRAVEL_FEATURES.LOUNGE_ACCESS]:        'lounge-access',
    [TRAVEL_FEATURES.MOBILE_INSURANCE]:     'mobile-insurance',
    [TRAVEL_FEATURES.NO_FOREX]:             'no-forex',
    [TRAVEL_FEATURES.PRICE_PROTECTION]:     'price-protection',
    [TRAVEL_FEATURES.PURCHASE_ASSURANCE]:   'purchase-assurance',
    [TRAVEL_FEATURES.TRAVEL_ACCIDENT]:      'travel-accident',
    [TRAVEL_FEATURES.TRAVEL_EMERGENCY]:     'travel-emergency',
    [TRAVEL_FEATURES.TRIP_CANCELLATION]:    'trip-cancellation',
    [TRAVEL_FEATURES.TRIP_INTERRUPTION]:    'trip-interruption',
    [TRAVEL_FEATURES.HOTEL_MOTEL_BURGLARY]: 'hotel-motel-burglary',
};

const DEPRECATED_YUKON_PROVINCE_CODE = 'YK';

const NETWORKS_NAMES = {
    [NETWORKS.AMEX]: NETWORKS.AMEX,
    [NETWORKS.MASTERCARD]: NETWORKS.MASTERCARD,
    [NETWORKS.VISA]: NETWORKS.VISA,
};

// Map scenario strings to the corresponding section names in sponsoredPlacements
const scenarioToSectionMap = {
    rewards: 'all_rewards',
    'low-interest': 'low_interest',
    'balance-transfer': 'balance_transfer',
    secured: 'secured',
};


export default fetchCreditCards;
